import { useRouter } from "@uirouter/react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import styled from "styled-components";
import imageSrc from "../../assets/vaults/stock-vault.png";
import { ErrorText, useHasCredit } from "../../components/billing";
import { PrimaryButton } from "../../components/button";
import { Form } from "../../components/forms";
import { EditableField } from "../../components/forms/field";
import { TextField } from "../../components/forms/text";
import { useAppLoader } from "../../components/loader/app-loader-hook";
import { AppStore } from "../../store";
import { AuthDTO } from "../../store/auth/models/dto";
import { useFetchable } from "../../store/common/fetchable";
import {
  Fetchable,
  isFetchableSuccess,
} from "../../store/common/fetchable/helpers";
import { Users } from "../../store/users/reducer";
import { VaultsDTO } from "../../store/vaults/models/dto";
import { VaultService } from "../../store/vaults/service";
import { getOption, useYupValidationResolver } from "../../utils/utils";
import { VaultsRoutes } from "./routes";

const Image = styled.img`
  display: block;
  width: 100%;
  margin-bottom: 2em;
`;

const Container = styled.div`
  width: 20em;
`;

const CTA = styled(PrimaryButton)`
  margin-top: 2em;
`;

type RequestDTO = VaultsDTO.Requests.Create.T;
const { useFields, defaults } = VaultsDTO.Requests.Create;

export const NewVault = () => {
  const { labels, validation } = useFields();
  const resolver = useYupValidationResolver(validation);
  const loader = useAppLoader();
  const currentUser = useFetchable(Users.current);
  const currentAuth = useSelector<
    AppStore,
    Fetchable<AuthDTO.Responses.Login.T>
  >((store) => store.auth.login);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestDTO>({
    resolver,
    defaultValues: defaults,
  });
  const router = useRouter();

  const onSubmit = handleSubmit(
    (data) =>
      loader(
        VaultService.addVault(data).then(() => {
          router.stateService.go(
            VaultsRoutes.names.vaultList,
            {},
            { reload: true }
          );
        })
      ) // TODO - catch error
  );

  const hasCredit = useHasCredit("VAULT_CREATION");
  const cta = useMemo(
    () =>
      getOption(currentUser).map((curr) => {
        return (
          <>
            <CTA
              disabled={
                !(hasCredit && curr.kycStatus === "VERIFIED") ||
                (isFetchableSuccess(currentAuth) &&
                  currentAuth.data.accessToken.roles.includes("ROLE_ADMIN"))
              }
              type="submit"
              variant="contained"
              fullWidth
            >
              Create it
            </CTA>
            {!hasCredit ? (
              <ErrorText>Please Top Up your Account</ErrorText>
            ) : null}
            {hasCredit && curr.kycStatus !== "VERIFIED" ? (
              <ErrorText>
                You cannot perform this action because your KYC status is not
                yet verified.
              </ErrorText>
            ) : null}
          </>
        );
      }),
    [hasCredit, hasCredit]
  );

  return cta.fold(
    () => null,
    (x) => (
      <Container>
        <Form onSubmit={onSubmit}>
          <Image src={imageSrc} />
          <EditableField editing control={control} name="friendlyName">
            <TextField
              fullWidth
              type="text"
              label={labels.friendlyName}
              error={!!errors.friendlyName}
              helperText={errors.friendlyName?.message}
            />
          </EditableField>
          {x}
        </Form>
      </Container>
    )
  );
};
